// Time Calculation Utilities
// Phase 2 - Week 4: Timesheet Processing Foundation

import { 
  TimeCalculation, 
  PayCalculation, 
  WeekBoundaries, 
  TIMESHEET_CONSTANTS,
  TimesheetValidationResult,
  TimesheetValidationError
} from '../types/timesheet.ts';

export class TimeCalculator {
  /**
   * Calculate total hours worked from start time, end time, and break time
   * @param startTime - Start time in HH:MM format (24-hour)
   * @param endTime - End time in HH:MM format (24-hour)
   * @param breakMinutes - Break time in minutes (default: 0)
   * @returns Total hours worked as a decimal number
   */
  static calculateHours(startTime: string, endTime: string, breakMinutes: number = 0): number {
    try {
      const startMinutes = this.parseTime(startTime);
      const endMinutes = this.parseTime(endTime);
      
      let totalMinutes = endMinutes - startMinutes;
      
      // Handle overnight shifts (end time next day)
      if (totalMinutes < 0) {
        totalMinutes += 24 * 60; // Add 24 hours in minutes
      }
      
      // Subtract break time
      totalMinutes -= breakMinutes;
      
      // Ensure non-negative result
      if (totalMinutes < 0) {
        totalMinutes = 0;
      }
      
      // Convert to hours with 2 decimal precision
      return Math.round((totalMinutes / 60) * 100) / 100;
    } catch (error) {
      console.error('Error calculating hours:', error);
      return 0;
    }
  }

  /**
   * Calculate overtime and double-time hours based on total hours worked
   * @param totalHours - Total hours worked in a day
   * @param weeklyHours - Total hours worked in the week so far (optional)
   * @returns Object with regular, overtime, and double-time hours
   */
  static calculateOvertimeHours(
    totalHours: number, 
    weeklyHours: number = 0
  ): { regular: number; overtime: number; doubleTime: number } {
    const { REGULAR_HOURS_PER_DAY, REGULAR_HOURS_PER_WEEK } = TIMESHEET_CONSTANTS;
    
    let regular = 0;
    let overtime = 0;
    let doubleTime = 0;
    
    // Daily overtime calculation (over 8 hours per day)
    if (totalHours <= REGULAR_HOURS_PER_DAY) {
      regular = totalHours;
    } else if (totalHours <= 12) {
      // 8 hours regular, remainder overtime
      regular = REGULAR_HOURS_PER_DAY;
      overtime = totalHours - REGULAR_HOURS_PER_DAY;
    } else {
      // 8 hours regular, 4 hours overtime, remainder double-time
      regular = REGULAR_HOURS_PER_DAY;
      overtime = 4;
      doubleTime = totalHours - 12;
    }
    
    // Weekly overtime calculation (over 40 hours per week)
    const totalWeeklyHours = weeklyHours + totalHours;
    if (totalWeeklyHours > REGULAR_HOURS_PER_WEEK) {
      const weeklyOvertimeHours = totalWeeklyHours - REGULAR_HOURS_PER_WEEK;
      const currentDayContribution = Math.min(totalHours, weeklyOvertimeHours);
      
      // Convert regular hours to overtime if weekly limit exceeded
      if (currentDayContribution > overtime + doubleTime) {
        const additionalOvertime = currentDayContribution - (overtime + doubleTime);
        regular = Math.max(0, regular - additionalOvertime);
        overtime += additionalOvertime;
      }
    }
    
    return {
      regular: Math.round(regular * 100) / 100,
      overtime: Math.round(overtime * 100) / 100,
      doubleTime: Math.round(doubleTime * 100) / 100
    };
  }

  /**
   * Calculate total pay including overtime and double-time premiums
   * @param regularHours - Regular hours worked
   * @param overtimeHours - Overtime hours worked
   * @param doubleTimeHours - Double-time hours worked
   * @param hourlyRate - Base hourly rate
   * @param workType - Type of work (affects rate calculation)
   * @returns Object with detailed pay breakdown
   */
  static calculatePay(
    regularHours: number,
    overtimeHours: number,
    doubleTimeHours: number,
    hourlyRate: number,
    workType: string = 'regular'
  ): PayCalculation {
    const { OVERTIME_MULTIPLIER, DOUBLE_TIME_MULTIPLIER } = TIMESHEET_CONSTANTS;
    
    // Apply work type multiplier to base rate
    let baseRate = hourlyRate;
    if (workType === 'holiday') {
      baseRate = hourlyRate * DOUBLE_TIME_MULTIPLIER; // Holiday pay is double-time
    }
    
    const overtimeRate = baseRate * OVERTIME_MULTIPLIER;
    const doubleTimeRate = baseRate * DOUBLE_TIME_MULTIPLIER;
    
    const regularPay = regularHours * baseRate;
    const overtimePay = overtimeHours * overtimeRate;
    const doubleTimePay = doubleTimeHours * doubleTimeRate;
    const totalPay = regularPay + overtimePay + doubleTimePay;
    
    return {
      regularPay: Math.round(regularPay * 100) / 100,
      overtimePay: Math.round(overtimePay * 100) / 100,
      doubleTimePay: Math.round(doubleTimePay * 100) / 100,
      totalPay: Math.round(totalPay * 100) / 100,
      hourlyRate: baseRate,
      overtimeRate: Math.round(overtimeRate * 100) / 100,
      doubleTimeRate: Math.round(doubleTimeRate * 100) / 100
    };
  }

  /**
   * Get week boundaries (Monday to Sunday) for a given date
   * @param date - Any date within the week
   * @returns Object with week start (Monday) and end (Sunday) dates
   */
  static getWeekDates(date: Date): WeekBoundaries {
    const d = new Date(date);
    const day = d.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    // Calculate days to subtract to get to Monday
    const daysToMonday = day === 0 ? 6 : day - 1;
    
    // Get Monday of the week
    const monday = new Date(d);
    monday.setDate(d.getDate() - daysToMonday);
    monday.setHours(0, 0, 0, 0);
    
    // Get Sunday of the week
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    
    // Calculate week number
    const startOfYear = new Date(monday.getFullYear(), 0, 1);
    const weekNumber = Math.ceil(((monday.getTime() - startOfYear.getTime()) / 86400000 + startOfYear.getDay() + 1) / 7);
    
    return {
      start: monday,
      end: sunday,
      weekNumber,
      year: monday.getFullYear()
    };
  }

  /**
   * Format minutes as HH:MM string
   * @param minutes - Total minutes
   * @returns Time string in HH:MM format
   */
  static formatTime(minutes: number): string {
    const hours = Math.floor(Math.abs(minutes) / 60);
    const mins = Math.abs(minutes) % 60;
    const sign = minutes < 0 ? '-' : '';
    
    return `${sign}${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }

  /**
   * Parse time string (HH:MM) to minutes
   * @param timeString - Time in HH:MM format (24-hour)
   * @returns Total minutes from midnight
   */
  static parseTime(timeString: string): number {
    if (!timeString || typeof timeString !== 'string') {
      throw new Error('Invalid time string');
    }
    
    const timeParts = timeString.trim().split(':');
    if (timeParts.length !== 2) {
      throw new Error('Time must be in HH:MM format');
    }
    
    const hours = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);
    
    if (isNaN(hours) || isNaN(minutes)) {
      throw new Error('Invalid time values');
    }
    
    if (hours < 0 || hours > 23) {
      throw new Error('Hours must be between 0 and 23');
    }
    
    if (minutes < 0 || minutes > 59) {
      throw new Error('Minutes must be between 0 and 59');
    }
    
    return hours * 60 + minutes;
  }

  /**
   * Format hours as readable string (e.g., "8.5 hours", "1.25 hours")
   * @param hours - Hours as decimal number
   * @returns Formatted string
   */
  static formatHours(hours: number): string {
    if (hours === 0) return '0 hours';
    if (hours === 1) return '1 hour';
    
    // Round to 2 decimal places and remove trailing zeros
    const rounded = Math.round(hours * 100) / 100;
    return `${rounded} hours`;
  }

  /**
   * Convert decimal hours to hours and minutes
   * @param decimalHours - Hours as decimal (e.g., 8.5)
   * @returns Object with hours and minutes
   */
  static decimalToHoursMinutes(decimalHours: number): { hours: number; minutes: number } {
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    
    return { hours, minutes };
  }

  /**
   * Validate timesheet entry data
   * @param data - Timesheet entry data to validate
   * @returns Validation result with errors and warnings
   */
  static validateTimesheetEntry(data: {
    startTime: string;
    endTime: string;
    breakTime?: number;
    date: Date;
    hourlyRate: number;
  }): TimesheetValidationResult {
    const errors: TimesheetValidationError[] = [];
    const warnings: TimesheetValidationError[] = [];

    try {
      // Validate time format
      const startMinutes = this.parseTime(data.startTime);
      const endMinutes = this.parseTime(data.endTime);
      
      // Validate break time
      const breakTime = data.breakTime || 0;
      if (breakTime < TIMESHEET_CONSTANTS.MIN_BREAK_MINUTES || breakTime > TIMESHEET_CONSTANTS.MAX_BREAK_MINUTES) {
        errors.push({
          field: 'breakTime',
          message: `Break time must be between ${TIMESHEET_CONSTANTS.MIN_BREAK_MINUTES} and ${TIMESHEET_CONSTANTS.MAX_BREAK_MINUTES} minutes`,
          value: breakTime
        });
      }
      
      // Calculate total hours
      const totalHours = this.calculateHours(data.startTime, data.endTime, breakTime);
      
      // Validate reasonable work hours
      if (totalHours > TIMESHEET_CONSTANTS.MAX_HOURS_PER_DAY) {
        errors.push({
          field: 'hours',
          message: `Total hours (${totalHours}) exceeds maximum allowed (${TIMESHEET_CONSTANTS.MAX_HOURS_PER_DAY}) per day`,
          value: totalHours
        });
      }
      
      if (totalHours <= 0) {
        errors.push({
          field: 'hours',
          message: 'Total hours must be greater than 0',
          value: totalHours
        });
      }
      
      // Validate hourly rate
      if (data.hourlyRate <= 0) {
        errors.push({
          field: 'hourlyRate',
          message: 'Hourly rate must be greater than 0',
          value: data.hourlyRate
        });
      }
      
      // Validate date (not in future)
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      if (data.date > today) {
        errors.push({
          field: 'date',
          message: 'Date cannot be in the future',
          value: data.date
        });
      }
      
      // Warnings for unusual patterns
      if (totalHours > 12) {
        warnings.push({
          field: 'hours',
          message: `Long work day detected (${totalHours} hours). Please verify accuracy.`,
          value: totalHours
        });
      }
      
      if (breakTime === 0 && totalHours > 6) {
        warnings.push({
          field: 'breakTime',
          message: 'No break time specified for work day longer than 6 hours',
          value: breakTime
        });
      }
      
    } catch (error) {
      errors.push({
        field: 'time',
        message: error instanceof Error ? error.message : 'Invalid time format',
        value: `${data.startTime} - ${data.endTime}`
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Calculate comprehensive time and pay information
   * @param startTime - Start time in HH:MM format
   * @param endTime - End time in HH:MM format
   * @param breakTime - Break time in minutes
   * @param hourlyRate - Base hourly rate
   * @param workType - Type of work
   * @param weeklyHours - Hours already worked this week
   * @returns Complete calculation result
   */
  static calculateComplete(
    startTime: string,
    endTime: string,
    breakTime: number,
    hourlyRate: number,
    workType: string = 'regular',
    weeklyHours: number = 0
  ): TimeCalculation & PayCalculation {
    const totalHours = this.calculateHours(startTime, endTime, breakTime);
    const { regular, overtime, doubleTime } = this.calculateOvertimeHours(totalHours, weeklyHours);
    const payCalc = this.calculatePay(regular, overtime, doubleTime, hourlyRate, workType);
    
    return {
      totalMinutes: Math.round(totalHours * 60),
      totalHours,
      regularHours: regular,
      overtimeHours: overtime,
      doubleTimeHours: doubleTime,
      ...payCalc
    };
  }
}

// Utility functions for common operations
export const timeUtils = {
  /**
   * Get current time in HH:MM format
   */
  getCurrentTime(): string {
    const now = new Date();
    return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
  },

  /**
   * Get today's date in YYYY-MM-DD format
   */
  getTodayDate(): string {
    const today = new Date();
    return today.toISOString().split('T')[0];
  },

  /**
   * Check if a date is today
   */
  isToday(date: Date): boolean {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  },

  /**
   * Check if a date is in the current week
   */
  isCurrentWeek(date: Date): boolean {
    const weekBoundaries = TimeCalculator.getWeekDates(new Date());
    return date >= weekBoundaries.start && date <= weekBoundaries.end;
  },

  /**
   * Format date for display
   */
  formatDate(date: Date): string {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  },

  /**
   * Format currency for display
   */
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }
};
